import { mobxSet } from '@src/slide/infrastructure/module/independent/assert/get-set';
import tdocsBridge from '@src/slide/infrastructure/module/independent/tdocsBridge/tdocsBridge';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { EventCodeType, TDWActionType, TDWFileType, TDWModuleType, TDWOperationType, TDWVer9Type } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';    // 数据上报
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { GraphType } from '@src/slide/infrastructure/module/dependent/shape-info/constant';
import { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import AttachmentUploader, { FILE_TYPE } from '@src/js/common/ark-extensions/dc-components/attachment-uploader';
import { getEditorInstanceByModel } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/utils';
import { dispatchResizeEvent } from '@frame-m/components/utils/dispatchResizeEvent';
import NativeSelectImage from './wework/native-select-image';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { SoftKeyboard } from '@tencent/docs-soft-keyboard';
import { ColorPanelType, EditPanelType } from '@com-m/edit-panel-m/const';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { isIpadExternalKeyboardOpen } from '../utils/soft-keyboard-height-manager';
import { ImageActionSheetOptionID } from '@src/slide/application/service/mobile-action-sheet-service/core/enum';
import { getToggleStatusByType, ToggleType } from '@src/slide/infrastructure/module/dependent/config/toggle-config';
import { MobileConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import DocsConfig from '@tencent/docs-config';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import { REPORT_LOCATION } from '@src/slide/application/service/ai-replace-slide/ai-replace-slide-service.interface';

const TextEditedModelTypeArr = [UIModelType.SHAPE, UIModelType.NOTE, UIModelType.TABLE];

const isSupportEditPanel = (model) => {
    if (TextEditedModelTypeArr.includes(model.type)) return true;
    if (model.type === UIModelType.GROUP) {
        return Array.from(model.children).some((item: {type: string; children: any}) => {
            if (item.type === UIModelType.GROUP) {
                return isSupportEditPanel(item.children);
            }
            return item.type === UIModelType.SHAPE;
        });
    }
    return false;
};

function getMobileActionSheetService() {
    return getAppServices().editableServiceLayer?.mobileActionSheetService?.promise();
}

function getAppServices(): IMobileEditorAppServices {
    return AppServiceProvider.getInstance().getAppServices() as
        IMobileEditorAppServices;
}

const tdwAddonListView = () => {
    tdw({
        opername: TDWOperationType.DOC_ADDON,
        module: TDWModuleType.ADDON_LIST,
        action: TDWActionType.LIST_VIEW,
        eventcode: EventCodeType.DOC_ADDON_ADDON_LIST_LIST_VIEW,
        ver9: TDWVer9Type.DETAIL_INSERT,
        file_type: TDWFileType.SLIDE,
    });
};

// 处理 toolbar-m 的一系列点按事件
const mobileToolBarHandlerMap = {
    [ActionType.AI_GENERATE_PAGE_MOBILE]() {
        getAppServices().editableServiceLayer?.aiFloatManager?.triggerGeneratePageMobile({});
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.PAGE_CREATE,
            action: TDWActionType.AI_CLK,
            ver5: TDWModuleType.MOBILE,
            ver6: '0',
        });
    },
    [ActionType.ADD_SLIDE]() {
        getAppServices().getUiDataService().presentation.addSlide();
    },
    [ActionType.INSERT_TEXT]() {
        // 触发当前正在编辑的文本框失焦
        getAppServices().viewableServiceLayer.selectionService.selectedElements.clear();
        // 为了防止连续点击生成空文本框，每次点击前先进行失去焦点操作，提前触发空文本框的删除
        const current = {
            left: (getAppServices().getUiDataService().presentation.props.slideWidth / 2) - 200,
            top: (getAppServices().getUiDataService().presentation.props.slideHeight / 2) - 80,
        };
        const model = {
            style: {
                position: 'absolute',
                left: current.left,
                top: current.top,
                bgShape: GraphType.RECT,
                width: 400,
                height: 160,
            },
            subType: 1,
        };
        const extraInfo = {
            textBodyStyle: {
                autoFitType: AutoFitType.SHAPE_AUTO_FIT,
            },
        };
        const { selectionService } = getAppServices().viewableServiceLayer;
        const currentSlideIndex = selectionService?.currentSlideIndex;
        const slides = selectionService?.slides;
        const slideModel = slides?.[currentSlideIndex];
        if (slideModel) {
            slideModel.addElement([UIModelType.SHAPE], [model], [extraInfo]);
            const currentSlideChild = selectionService.currentSlide?.children || [];
            const shape = currentSlideChild.length && currentSlideChild[currentSlideChild.length - 1];
            shape && mobxSet(shape, 'initFocus', true);
            dispatchResizeEvent({ scrollCenter: true }); // 插入文本框后，使页面滚动到画布中心

            // 新交互，创建后直接进入编辑态
            if (shape) {
                (shape as IShapeModel).editorInstance?.setFocus?.();
            }
        }
    },
    [ActionType.INSERT_SHAPE]() {
        const { editableServiceLayer } = getAppServices();
        if (!editableServiceLayer) return;
        const { mobileToolbarEditManager } = editableServiceLayer;
        const editPanelShow = mobileToolbarEditManager.getEditPanelShowState();
        // 获取到当前正在编辑的 TextEditor 实例
        const textEditor = getAppServices().viewableServiceLayer.editorInstanceService.getEditorInstance();
        if (editPanelShow) {
            // 触发当前正在编辑的文本框失焦
            textEditor?.triggerBlur();
        }
    },
    [ActionType.INSERT_PICTURE]() {
        const element = document.activeElement;
        (element as HTMLElement)?.blur(); // 某些情况会不收起键盘，强制失焦点
        // 插入图片数据上报
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.M_EDIT_MODE,
            action: TDWActionType.M_IMG_INSERT,
        });
        getAppServices().viewableServiceLayer.toolbarStateManager.lastInsertButtonClick = 1;
        setTimeout(() => {
            // 未升级企业不支持window.tdocs.chooseImage方法 导致插入图片按钮点击无反应
            if (
                config.supportEnterprise(EnterPriseType.APP)
                && !config.supportEnterprise(EnterPriseType.APP_PC)
                && tdocsBridge.tdocs?.chooseImage
            ) {
                NativeSelectImage.selectImage((images) => {
                    if (images && images.length > 0) {
                        this.handleImageChange(images);
                    }
                });
            } else {
                if (getToggleStatusByType(ToggleType.TDOCS_PICTURE_AUDIO_AND_VIDEO_SWITCH, true)
                    && (DocsConfig.getInstance().getConfig(MobileConfig.insertNewPictureAudioVideo)
                        ?? false)) {
                    getMobileActionSheetService()?.then((service) => {
                        service.imageActionSheet.callActionSheet([
                            ImageActionSheetOptionID.FromTencentDocument,
                            ImageActionSheetOptionID.FromLocalFile,
                        ]);
                    });
                } else {
                    getMobileActionSheetService()?.then((service) => {
                        service.imageActionSheet.callActionDirect(ImageActionSheetOptionID.FromLocalFile);
                    });
                }
            }
        }, 600); // 短暂延迟是因为某些机型无法收起键盘
    },
    [ActionType.EVOKE_KEYBOARD]() {
        const model = getAppServices().viewableServiceLayer.selectionService?.selectedElements.getCurrentModel();
        const editorInstance = getEditorInstanceByModel(model);

        if (editorInstance) {
            if (getAppServices().viewableServiceLayer.editorInstanceService.checkEditorIsEditing()) {
                // 如果在编辑态，说明目前已有选中的文本，直接触发文本的 focus 即可
                editorInstance.setFocus();
            } else {
                if (model.type === UIModelType.TABLE) {
                    model.enterFirstCell?.();
                }
                if (model?.isPlaceholder) {
                    const enterPlaceHolderEvent = new CustomEvent('enterPlaceHolder', {
                        detail: model,
                    });
                    window.dispatchEvent(enterPlaceHolderEvent);
                } else {
                    editorInstance.setFocus?.();
                }
            }

            // 打开键盘时不立即关闭文字编辑面板，等待下次键盘变化之后再关闭，避免页面抖动
            let isEditPanelClosed = false;
            const softKeyboard = new SoftKeyboard();
            softKeyboard.on('change', (keyboard) => {
                if (keyboard.isSupported && !isEditPanelClosed) {
                    isEditPanelClosed = true;
                    const { editableServiceLayer } = getAppServices();
                    if (!editableServiceLayer) return;
                    const { mobileToolbarEditManager } = editableServiceLayer;
                    // 此时键盘已经出现，但是文字编辑面板还没有关闭，所以需要立即把工具栏的高度置为不包含文字编辑面板的高度，
                    // 否则会有一瞬间键盘和文字编辑面板共存，会有闪烁
                    mobileToolbarEditManager.forceHideEditPanel();
                    // 此时才真正从数据上关闭文字编辑面板
                    mobileToolbarEditManager.closeEditPanel();
                }
                softKeyboard?.dispose();
            });
            setTimeout(() => {
                // 兜底逻辑，如果不支持 softKeyboard，那么 500ms 之后关闭
                if (!isEditPanelClosed) {
                    isEditPanelClosed = true;
                    const { editableServiceLayer } = getAppServices();
                    if (!editableServiceLayer) return;
                    editableServiceLayer.mobileToolbarEditManager.closeEditPanel();
                    softKeyboard?.dispose();
                }
            }, 500);
        }
    },
    [ActionType.PACKUP_KEYBOARD]() {
        const model = getAppServices().viewableServiceLayer.selectionService?.selectedElements.getCurrentModel();
        if (TextEditedModelTypeArr.includes(model.type)) {
            getEditorInstanceByModel(model)?.triggerBlur?.();
        }
    },
    [ActionType.UPLOAD_LOCAL_FILE]() {
        const uploadLocalFileNoLimit = getCacConfigByKey('custom.mobile.uploadLocalFileNoLimit', false);
        if (uploadLocalFileNoLimit) {
            AttachmentUploader.getInstance().upload();
        } else {
            AttachmentUploader.getInstance().upload({ acceptFileType: FILE_TYPE });
        }
        tdw({
            opername: TDWOperationType.DOC_WORD_EDITOR,
            module: TDWModuleType.LOCAL_FILES,
            action: TDWActionType.UPLOAD_CLICK,
        });
    },
    [ActionType.NOTE]() {
        const { noteAreaManager } = getAppServices().viewableServiceLayer;
        if (noteAreaManager?.show) {
            const slide = getAppServices().viewableServiceLayer.selectionService?.currentSlide;
            const shapeModel = slide?.noteData?.noteTextBody;
            shapeModel?.editorInstance?.triggerBlur?.({ needUpdate: true });
            noteAreaManager.leaveMobileFullScreen();
            noteAreaManager.setHide();

            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.M_MOBILE_BUTTON,
                action: TDWActionType.M_REMARKS_EDIT_OFF,
            });
        } else {
            noteAreaManager.setShow();

            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.M_MOBILE_BUTTON,
                action: TDWActionType.M_REMARKS_EDIT_ON,
            });
        }
    },
    [ActionType.LAYOUT_SUGGESTION]() {
        const { editableServiceLayer } = getAppServices();
        if (!editableServiceLayer) return;
        const { mobileToolbarEditManager } = editableServiceLayer;
        const editPanelShow = mobileToolbarEditManager.getEditPanelShowState();
        // 获取到当前正在编辑的 TextEditor 实例
        const textEditor = getAppServices().viewableServiceLayer.editorInstanceService.getEditorInstance();
        if (editPanelShow) {
            // 触发当前正在编辑的文本框失焦
            textEditor?.triggerBlur();
        }

        // 非 ipad 外接键盘时需要收起键盘
        if (!isIpadExternalKeyboardOpen()) {
            // 强制失去焦点，从而收起键盘
            (document.activeElement as HTMLElement)?.blur();
            // 仍然展示光标（如果是光标态的话），用于标识当前样式生效位置
            textEditor?.editor?.startKeepCaretShow();
        }

        getAppServices().editableServiceLayer?.mobileRatingService?.promise().then((ratingService) => {
            ratingService.showInEdit();
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.AUTO_BEAUTIFY,
                action: TDWActionType.BEAUTIFY_M_CLICK,
                ver5: 1,
            });
        });
    },
    [ActionType.AI_REPLACE_SLIDE]() {
        // copy from LAYOUT_SUGGESTION
        const { editableServiceLayer } = getAppServices();
        if (!editableServiceLayer) return;
        const { mobileToolbarEditManager } = editableServiceLayer;
        const editPanelShow = mobileToolbarEditManager.getEditPanelShowState();
        // 获取到当前正在编辑的 TextEditor 实例
        const textEditor = getAppServices().viewableServiceLayer.editorInstanceService.getEditorInstance();
        if (editPanelShow) {
            // 触发当前正在编辑的文本框失焦
            textEditor?.triggerBlur();
        }

        // 非 ipad 外接键盘时需要收起键盘
        if (!isIpadExternalKeyboardOpen()) {
            // 强制失去焦点，从而收起键盘
            (document.activeElement as HTMLElement)?.blur();
            // 仍然展示光标（如果是光标态的话），用于标识当前样式生效位置
            textEditor?.editor?.startKeepCaretShow();
        }

        mobileToolbarEditManager.openEditPanel(null, EditPanelType.AI_REPLACE_SLIDE);
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: TDWModuleType.PAGE_BEAUTY,
            action: TDWActionType.CLK,
            ver5: TDWModuleType.MOBILE,
            ver6: REPORT_LOCATION.mobileToolbar,
        });
    },
    [ActionType.FORECOLOR]() {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.M_EDIT_FORMAT,
            action: TDWActionType.M_EDIT_FORECOLOR,
        });
    },
    [ActionType.TEXT_EDIT_PANEL]() {
        const model = getAppServices().viewableServiceLayer.selectionService.selectedElements.getCurrentModel();
        const { editableServiceLayer } = getAppServices();
        if (!editableServiceLayer) return;
        const { mobileToolbarEditManager } = editableServiceLayer;
        const isEditPanelShow = mobileToolbarEditManager.getEditPanelShowState();

        // 已经打开加号面板，只需要切换到文字编辑面板
        if (isEditPanelShow && mobileToolbarEditManager.editPanelType !== EditPanelType.TEXT_EDIT) {
            mobileToolbarEditManager.setEditPanelType(EditPanelType.TEXT_EDIT);
            mobileToolbarEditManager.setColorPanelType(ColorPanelType.NONE);
            return;
        }

        // 已经打开颜色面板
        if (isEditPanelShow && mobileToolbarEditManager.colorPanelType !== ColorPanelType.NONE) {
            mobileToolbarEditManager.setColorPanelType(ColorPanelType.NONE);
            return;
        }

        // canvas版本开闭文字编辑面板
        if (model && (isSupportEditPanel(model))) {
            const editPanelShow = mobileToolbarEditManager.getEditPanelShowState();
            // 获取到当前正在编辑的 TextEditor 实例
            const textEditor = getAppServices().viewableServiceLayer.editorInstanceService.getEditorInstance();
            if (editPanelShow) {
                // 触发当前正在编辑的文本框失焦
                textEditor?.triggerBlur();
                mobileToolbarEditManager.closeEditPanel();
            } else {
                mobileToolbarEditManager.openEditPanel(model, EditPanelType.TEXT_EDIT);
                // 非 ipad 外接键盘时需要收起键盘
                if (!isIpadExternalKeyboardOpen()) {
                    // 强制失去焦点，从而收起键盘
                    (document.activeElement as HTMLElement)?.blur();
                    // 仍然展示光标（如果是光标态的话），用于标识当前样式生效位置
                    textEditor?.editor?.startKeepCaretShow();
                }
            }
        }
    },
    [ActionType.ADD_EDIT_PANEL]() {
        const textEditor = getAppServices().viewableServiceLayer.editorInstanceService.getEditorInstance();
        const model = getAppServices().viewableServiceLayer.selectionService.selectedElements.getCurrentModel();
        const { editableServiceLayer } = getAppServices();
        if (!editableServiceLayer) return;
        const { mobileToolbarEditManager } = editableServiceLayer;
        const isEditPanelShow = mobileToolbarEditManager.getEditPanelShowState();

        // 已经打开文字编辑面板，只需要切换到加号面板
        if (isEditPanelShow && mobileToolbarEditManager.editPanelType !== EditPanelType.ADD_ADIT) {
            mobileToolbarEditManager.setEditPanelType(EditPanelType.ADD_ADIT);
            tdwAddonListView();
            return;
        }

        if (isEditPanelShow && mobileToolbarEditManager.colorPanelType !== ColorPanelType.NONE) {
            mobileToolbarEditManager.setEditPanelType(EditPanelType.ADD_ADIT);
            mobileToolbarEditManager.setColorPanelType(ColorPanelType.NONE);
            return;
        }

        // canvas版本开闭加号面板
        const editPanelShow = mobileToolbarEditManager.getEditPanelShowState();
        // 获取到当前正在编辑的 TextEditor 实例
        if (editPanelShow) {
            // 触发当前正在编辑的文本框失焦
            textEditor?.triggerBlur();
            mobileToolbarEditManager.closeEditPanel();
        } else {
            mobileToolbarEditManager.openEditPanel(model, EditPanelType.ADD_ADIT);
            // 非 ipad 外接键盘时需要收起键盘
            if (!isIpadExternalKeyboardOpen()) {
                // 强制失去焦点，从而收起键盘
                (document.activeElement as HTMLElement)?.blur();
                // 仍然展示光标（如果是光标态的话），用于标识当前样式生效位置
                textEditor?.editor?.startKeepCaretShow();
            }
            tdwAddonListView();
        }
    },
};

export default mobileToolBarHandlerMap;
